/**
 * The curated master catalogue vendors pick from.
 *
 * A vendor listing links to a master rather than copying it, so a correction here
 * reaches every vendor selling that product and every shopper looking at it. That
 * is also the risk: an edit is not a local change, which is why every row shows how
 * many vendors it reaches before you touch it.
 */
import type { StatusTone } from '@/components/ui/status-pill';

export type Vertical = 'FOOD' | 'PHARMACY';

/** Where the fee that applies to a master came from. */
export type FeeSource = 'CATALOG' | 'CATEGORY' | 'NONE';

export type CatalogProduct = {
  id: string;
  vertical: Vertical;
  name: string;
  brand: string | null;
  category: string;
  pack_size: string | null;
  description: string | null;
  is_prescription_required: boolean;
  /**
   * The gallery, in display order. The first is what shoppers see on cards and in
   * search; the rest are what they swipe through on the product page.
   */
  images: { id: string; path: string }[];
  /** The first gallery photo, kept for callers that only ever wanted one. */
  image_path: string | null;
  /**
   * The fee set on THIS product, and whether it overrides its category.
   *
   * The screen showed neither, so an admin could not tell whether a product carried its own
   * rate or was riding the category default — nor see the number either way.
   */
  commission: {
    mode: 'RATE' | 'FLAT' | null;
    rate_pct: number | null;
    amount_inr: number | null;
    is_override: boolean;
  };
  /**
   * The fee that actually applies — this product's own, or its category's.
   *
   * Distinct from `commission`: "riding the Dry Food default" and "the Dry Food default is
   * 10%" are two different facts, and the screen needs both.
   */
  effective: {
    mode: 'RATE' | 'FLAT' | null;
    rate_pct: number | null;
    amount_inr: number | null;
    source: FeeSource;
  };
  /**
   * How many vendor listings hang off this master.
   *
   * The blast radius of any edit on this screen, and the reason to think twice before
   * retiring a row. It used to be fetched one product at a time, only after you had
   * already opened one — which is after the moment you needed it.
   */
  vendor_count: number;
  is_active: boolean;
  updated_at: string;
};

export type CatalogListResponse = {
  items: CatalogProduct[];
  total: number;
  page: number;
  page_size: number;
};

export type CatalogQuery = {
  vertical?: Vertical | '';
  category?: string;
  q?: string;
  page?: number;
  page_size?: number;
  is_active?: string;
  has_own_fee?: string;
  no_photos?: string;
  prescription?: string;
  unused?: string;
  /** A window on when the master was last changed. */
  from?: string;
  to?: string;
};

/** What the tiles above the catalogue count. */
export type CatalogCounts = {
  total: number;
  live: number;
  retired: number;
  unused: number;
  no_photos: number;
  own_fee: number;
  prescription: number;
  /** Masters charging nothing: no fee of their own, and no rule on their category. */
  no_fee: number;
  /** Vendor listings hanging off the catalogue at all — the reach of the whole thing. */
  linked_listings: number;
};

export type CreateCatalogInput = {
  vertical: Vertical;
  name: string;
  brand?: string | null;
  category: string;
  pack_size?: string | null;
  description?: string | null;
  is_prescription_required?: boolean;
};

export type UpdateCatalogInput = Partial<Omit<CreateCatalogInput, 'vertical'>> & {
  is_active?: boolean;
};

export const CATALOG_SORT_FIELDS = ['name', 'updated_at', 'category'] as const;

export const VERTICAL_LABEL: Record<Vertical, string> = {
  FOOD: 'Food & products',
  PHARMACY: 'Medicines',
};

export const FEE_SOURCE_LABEL: Record<FeeSource, string> = {
  CATALOG: 'Set here',
  CATEGORY: 'Category default',
  NONE: 'No fee anywhere',
};

export const FEE_SOURCE_TONE: Record<FeeSource, StatusTone> = {
  CATALOG: 'warning',
  CATEGORY: 'neutral',
  NONE: 'destructive',
};

/** A fee as one readable phrase — "10%" or "₹40". */
export function fee_amount(f: {
  mode: 'RATE' | 'FLAT' | null;
  rate_pct: number | null;
  amount_inr: number | null;
}): string {
  if (!f.mode) return '—';
  return f.mode === 'FLAT' ? `₹${f.amount_inr ?? 0}` : `${f.rate_pct ?? 0}%`;
}

/** How many photos a master actually has, counting the legacy single-image column. */
export function photo_count(p: CatalogProduct): number {
  return p.images.length || (p.image_path ? 1 : 0);
}

/**
 * What retiring or editing this master would touch, in words.
 *
 * Spelled out rather than left as a bare number because "3" beside a delete button does
 * not read as "three shops are selling this right now".
 */
export function reach(p: Pick<CatalogProduct, 'vendor_count'>): string {
  if (p.vendor_count === 0) return 'No vendor sells this yet';
  return `${p.vendor_count} vendor${p.vendor_count === 1 ? '' : 's'} sell${
    p.vendor_count === 1 ? 's' : ''
  } this — an edit reaches all of them and their customers`;
}
